import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ISO_CURRENCY,
  PROPOSAL_PAYMENT_PLANS,
  type InvoiceDto,
  type ProposalCurrency,
  type ProposalDto,
  type ProposalPaymentPlan,
  type RequestDto,
} from '@shared';
import { apiGet, apiPatch, apiPost, ApiError } from '../../lib/api';
import { ProposalStatusChip } from '../../components/ProposalStatusChip';

// Operator-side proposal page. One component handles both:
//   - new draft     (route: /admin/proposals/new?request_id=OMG-XXXX)
//   - existing      (route: /admin/proposals/:id)
//
// Drafts render the full editable form. Sent/accepted/declined/withdrawn/expired
// proposals render a read-only summary with whatever lifecycle action remains
// (Withdraw on sent, nothing on the terminal states).

interface DeliverableInput {
  label: string;
  description: string;
}
interface MilestoneInput {
  label: string;
  amount: string; // captured in £ (string for free typing), converted at submit
  due_at: string; // YYYY-MM-DD
}

interface FormState {
  scope_markdown: string;
  deliverables: DeliverableInput[];
  assumptions: string;
  fee: string;
  currency: ProposalCurrency;
  payment_plan: ProposalPaymentPlan;
  milestones: MilestoneInput[];
  success_fee_terms: string;
  valid_until: string; // YYYY-MM-DD
}

const PLAN_LABEL: Record<ProposalPaymentPlan, string> = {
  '50_50': '50 / 50',
  '30_40_30': '30 / 40 / 30',
  milestones: 'Milestones',
  success_fee: 'Success fee',
};

const STATUS_HELP: Record<string, string> = {
  draft: 'Drafts are staff-internal — the client cannot see this until you click Send.',
  sent: 'Proposal is with the client. They can accept or decline; you can still withdraw.',
  accepted: 'Client accepted. The parent brief has been advanced to engagement.',
  declined: 'Client declined. The parent brief stays in reviewing for triage.',
  withdrawn: 'You withdrew this proposal. To re-engage the client, draft a new one.',
  expired: 'The valid-until date passed before the client responded.',
};

function emptyForm(): FormState {
  // Default valid-until = 14 days out so operators don't ship a same-day expiry.
  const d = new Date();
  d.setDate(d.getDate() + 14);
  return {
    scope_markdown: '',
    deliverables: [{ label: '', description: '' }],
    assumptions: '',
    fee: '',
    currency: 'GBP',
    payment_plan: '50_50',
    milestones: [],
    success_fee_terms: '',
    valid_until: d.toISOString().slice(0, 10),
  };
}

function formFromDto(p: ProposalDto): FormState {
  return {
    scope_markdown: p.scope_markdown,
    deliverables: p.deliverables.length
      ? p.deliverables.map((d) => ({ label: d.label, description: d.description ?? '' }))
      : [{ label: '', description: '' }],
    assumptions: p.assumptions ?? '',
    fee: (p.fee_pence / 100).toString(),
    currency: p.currency,
    payment_plan: p.payment_plan,
    milestones: p.milestones.map((m) => ({
      label: m.label,
      amount: (m.amount_pence / 100).toString(),
      due_at: m.due_at ? m.due_at.slice(0, 10) : '',
    })),
    success_fee_terms: p.success_fee_terms ?? '',
    valid_until: p.valid_until.slice(0, 10),
  };
}

// Build POST/PATCH body. Throws if numeric parsing fails so caller can surface.
function toPayload(f: FormState): {
  scope_markdown: string;
  deliverables: { label: string; description?: string }[];
  assumptions?: string;
  fee_pence: number;
  currency: ProposalCurrency;
  payment_plan: ProposalPaymentPlan;
  milestones?: { label: string; amount_pence: number; due_at?: string | null }[];
  success_fee_terms?: string;
  valid_until: string;
} {
  const fee = Number(f.fee);
  if (!Number.isFinite(fee) || fee < 0) throw new Error('Fee must be a non-negative number');

  const deliverables = f.deliverables
    .filter((d) => d.label.trim() !== '')
    .map((d) => ({ label: d.label.trim(), description: d.description.trim() || undefined }));

  let milestones: { label: string; amount_pence: number; due_at?: string | null }[] | undefined;
  if (f.payment_plan === 'milestones') {
    milestones = f.milestones
      .filter((m) => m.label.trim() !== '' || m.amount.trim() !== '')
      .map((m) => {
        const amt = Number(m.amount);
        if (!Number.isFinite(amt) || amt < 0) throw new Error('Milestone amounts must be non-negative numbers');
        return {
          label: m.label.trim(),
          amount_pence: Math.round(amt * 100),
          due_at: m.due_at ? new Date(m.due_at).toISOString() : null,
        };
      });
    if (milestones.length === 0) throw new Error('Milestones plan requires at least one milestone');
  }

  return {
    scope_markdown: f.scope_markdown,
    deliverables,
    assumptions: f.assumptions.trim() || undefined,
    fee_pence: Math.round(fee * 100),
    currency: f.currency,
    payment_plan: f.payment_plan,
    milestones,
    success_fee_terms:
      f.payment_plan === 'success_fee' ? f.success_fee_terms.trim() || undefined : undefined,
    valid_until: new Date(f.valid_until).toISOString(),
  };
}

function formatMoney(amount_pence: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency, maximumFractionDigits: 0 })
      .format(amount_pence / 100);
  } catch {
    return `${currency} ${(amount_pence / 100).toLocaleString()}`;
  }
}

export function AdminProposalDetail() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const mode: 'new' | 'edit' = id ? 'edit' : 'new';
  const requestIdFromQuery = searchParams.get('request_id');

  const [proposal, setProposal] = useState<ProposalDto | null>(null);
  const [parentRequest, setParentRequest] = useState<RequestDto | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<null | 'save' | 'send' | 'withdraw'>(null);
  const [loading, setLoading] = useState(mode === 'edit');

  // Load existing proposal in edit mode.
  useEffect(() => {
    if (mode !== 'edit' || !id) return;
    setLoading(true);
    apiGet<{ proposal: ProposalDto }>(`/api/proposals/${id}`)
      .then((r) => {
        setProposal(r.proposal);
        setForm(formFromDto(r.proposal));
      })
      .catch((err) => setError(err instanceof ApiError ? err.serverMessage : 'Could not load proposal.'))
      .finally(() => setLoading(false));
  }, [id, mode]);

  // Load parent request context — fee-floor signal + headline. New mode needs
  // a request_id from the query string; edit mode pulls it from the proposal.
  useEffect(() => {
    const rid = mode === 'new' ? requestIdFromQuery : proposal?.request_id;
    if (!rid) return;
    apiGet<{ request: RequestDto }>(`/api/requests/${rid}`)
      .then((r) => setParentRequest(r.request))
      .catch(() => setParentRequest(null));
  }, [mode, requestIdFromQuery, proposal?.request_id]);

  const isDraft = mode === 'new' || proposal?.status === 'draft';
  const isSent = proposal?.status === 'sent';

  const feeFloorPence = useMemo(() => {
    if (!parentRequest?.value_pence) return null;
    return Math.ceil(parentRequest.value_pence * 0.02);
  }, [parentRequest?.value_pence]);

  // Mode guard — new mode requires a request_id.
  if (mode === 'new' && !requestIdFromQuery) {
    return (
      <>
        <h1 className="portal-h1">New proposal</h1>
        <p className="portal-sub">
          A proposal must be anchored to a brief. Open one from{' '}
          <Link to="/admin/inbox">the inbox</Link> and click <strong>Build proposal</strong>.
        </p>
      </>
    );
  }

  if (loading) return <p className="portal-sub">Loading…</p>;
  if (mode === 'edit' && !proposal && error) {
    return (
      <>
        <h1 className="portal-h1">Proposal not found</h1>
        <p className="portal-sub">{error}</p>
        <Link className="btn ghost" to="/admin/proposals" style={{ marginTop: 24 }}>← All proposals</Link>
      </>
    );
  }

  async function handleSave() {
    if (busy) return;
    setBusy('save');
    setError(null);
    try {
      const payload = toPayload(form);
      if (mode === 'new') {
        const body = { ...payload, request_id: requestIdFromQuery };
        const r = await apiPost<{ proposal: ProposalDto }>('/api/proposals', body);
        // Navigate to the canonical detail URL so refresh stays on the draft.
        navigate(`/admin/proposals/${r.proposal.id}`, { replace: true });
      } else if (proposal) {
        const r = await apiPatch<{ proposal: ProposalDto }>(`/api/proposals/${proposal.id}`, payload);
        setProposal(r.proposal);
        setForm(formFromDto(r.proposal));
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.serverMessage : err instanceof Error ? err.message : 'Save failed');
    } finally {
      setBusy(null);
    }
  }

  async function handleSend() {
    if (!proposal || busy) return;
    if (!window.confirm(`Send proposal ${proposal.id} to the client? They will be able to accept or decline it.`)) return;
    setBusy('send');
    setError(null);
    try {
      const r = await apiPost<{ proposal: ProposalDto }>(`/api/proposals/${proposal.id}/send`, {});
      setProposal(r.proposal);
    } catch (err) {
      setError(err instanceof ApiError ? err.serverMessage : 'Send failed');
    } finally {
      setBusy(null);
    }
  }

  async function handleWithdraw() {
    if (!proposal || busy) return;
    if (!window.confirm(`Withdraw proposal ${proposal.id}? The client will no longer be able to accept it.`)) return;
    setBusy('withdraw');
    setError(null);
    try {
      const r = await apiPost<{ proposal: ProposalDto }>(`/api/proposals/${proposal.id}/withdraw`, {});
      setProposal(r.proposal);
    } catch (err) {
      setError(err instanceof ApiError ? err.serverMessage : 'Withdraw failed');
    } finally {
      setBusy(null);
    }
  }

  const refDisplay = proposal?.id ?? '— draft —';
  const requestRefDisplay = parentRequest?.id ?? requestIdFromQuery ?? proposal?.request_id ?? '—';

  return (
    <>
      <div style={{ fontSize: 13, color: 'var(--fg-mute)', marginBottom: 8 }}>
        <Link to="/admin/proposals" style={{ borderBottom: '1px solid var(--rule)' }}>Proposals</Link>
        {' / '}<span className="rid">{refDisplay}</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h1 className="portal-h1">
            {mode === 'new' ? 'New proposal' : `Proposal ${proposal?.id}`}
          </h1>
          <p className="portal-sub">
            Anchored to brief <Link to={`/admin/inbox`} className="rid" style={{ borderBottom: '1px solid var(--rule)' }}>{requestRefDisplay}</Link>
            {parentRequest && <> · {parentRequest.headline}</>}
          </p>
        </div>
        {proposal && <ProposalStatusChip status={proposal.status} />}
      </div>

      {proposal && (
        <p className="portal-sub" style={{ marginTop: 14, color: 'var(--fg-soft)' }}>
          {STATUS_HELP[proposal.status]}
        </p>
      )}

      {error && (
        <div className="card" style={{ marginTop: 16, borderColor: 'var(--red)', color: 'var(--red)' }}>
          {error}
        </div>
      )}

      {/* ── editor (drafts only) ─────────────────────────────────────── */}
      {isDraft ? (
        <DraftEditor
          form={form}
          setForm={setForm}
          feeFloorPence={feeFloorPence}
          parentValuePence={parentRequest?.value_pence ?? null}
          busy={busy !== null}
        />
      ) : (
        proposal && <ReadOnlyView p={proposal} />
      )}

      {/* ── Finance hand-off: issue invoices from accepted proposals ───── */}
      {proposal && proposal.status === 'accepted' && <IssueInvoicePanel proposal={proposal} />}

      {/* ── footer actions ───────────────────────────────────────────── */}
      <div className="proposal-actions">
        <Link to="/admin/proposals" className="btn ghost" aria-disabled={busy !== null}>
          ← Back to list
        </Link>
        <div style={{ flex: 1 }} />
        {isDraft && (
          <button type="button" className="btn ghost" onClick={handleSave} disabled={busy !== null}>
            {busy === 'save' ? 'Saving…' : mode === 'new' ? 'Save draft' : 'Save changes'}
          </button>
        )}
        {isDraft && mode === 'edit' && (
          <button type="button" className="btn" onClick={handleSend} disabled={busy !== null}>
            {busy === 'send' ? 'Sending…' : 'Send to client →'}
          </button>
        )}
        {isSent && (
          <button type="button" className="btn ghost" onClick={handleWithdraw} disabled={busy !== null}>
            {busy === 'withdraw' ? 'Withdrawing…' : 'Withdraw'}
          </button>
        )}
      </div>
    </>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────

function IssueInvoicePanel({ proposal }: { proposal: ProposalDto }) {
  const navigate = useNavigate();
  const [existing, setExisting] = useState<InvoiceDto[] | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [amount, setAmount] = useState<string>((proposal.fee_pence / 100).toString());
  const [milestone, setMilestone] = useState<string>('');
  const [dueInDays, setDueInDays] = useState<string>('14');
  const [notes, setNotes] = useState<string>('');

  useEffect(() => {
    apiGet<{ invoices: InvoiceDto[] }>(`/api/invoices?proposal_id=${proposal.id}`)
      .then((r) => setExisting(r.invoices))
      .catch(() => setExisting([]));
  }, [proposal.id]);

  async function handleIssue() {
    setBusy(true);
    setError(null);
    try {
      const amt = Number(amount);
      if (!Number.isFinite(amt) || amt <= 0) throw new Error('Amount must be a positive number');
      const r = await apiPost<{ invoice: InvoiceDto }>('/api/invoices', {
        proposal_id: proposal.id,
        amount_pence: Math.round(amt * 100),
        milestone_label: milestone.trim() || undefined,
        due_in_days: Number(dueInDays) || 14,
        notes: notes.trim() || undefined,
      });
      navigate(`/admin/invoices/${r.invoice.id}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.serverMessage : err instanceof Error ? err.message : 'Issue failed');
    } finally {
      setBusy(false);
    }
  }

  const totalIssued = (existing ?? [])
    .filter((inv) => inv.status !== 'void')
    .reduce((s, inv) => s + inv.amount_pence, 0);
  const remaining = Math.max(0, proposal.fee_pence - totalIssued);

  return (
    <div className="card" style={{ marginTop: 20 }}>
      <h3 className="proposal-section-h">Invoices</h3>
      {existing === null ? (
        <p className="portal-sub">Loading…</p>
      ) : existing.length === 0 ? (
        <p className="proposal-prose" style={{ marginBottom: 12 }}>No invoices issued yet against this proposal.</p>
      ) : (
        <ul className="proposal-list">
          {existing.map((inv) => (
            <li key={inv.id}>
              <Link to={`/admin/invoices/${inv.id}`} className="rid" style={{ borderBottom: '1px solid var(--rule)' }}>{inv.id}</Link>
              {' — '}
              {new Intl.NumberFormat('en-GB', { style: 'currency', currency: inv.currency, maximumFractionDigits: 0 }).format(inv.amount_pence / 100)}
              {' · '}
              <span style={{ color: 'var(--fg-mute)' }}>{inv.status}</span>
              {inv.milestone_label && <span style={{ color: 'var(--fg-soft)' }}> · {inv.milestone_label}</span>}
            </li>
          ))}
        </ul>
      )}
      <div style={{ fontSize: 12, color: 'var(--fg-mute)', margin: '8px 0 14px' }}>
        Proposal value: {new Intl.NumberFormat('en-GB', { style: 'currency', currency: proposal.currency, maximumFractionDigits: 0 }).format(proposal.fee_pence / 100)}
        {' · '} Issued (excl. void): {new Intl.NumberFormat('en-GB', { style: 'currency', currency: proposal.currency, maximumFractionDigits: 0 }).format(totalIssued / 100)}
        {' · '} Remaining: {new Intl.NumberFormat('en-GB', { style: 'currency', currency: proposal.currency, maximumFractionDigits: 0 }).format(remaining / 100)}
      </div>

      {!open && (
        <button type="button" className="btn" onClick={() => setOpen(true)} disabled={remaining <= 0 && (existing?.length ?? 0) > 0}>
          + Issue invoice
        </button>
      )}

      {open && (
        <div className="prop-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div>
            <label className="prop-lbl" htmlFor="inv-amt">Amount ({proposal.currency})</label>
            <input
              id="inv-amt"
              type="number"
              inputMode="decimal"
              min="0"
              step="any"
              className="prop-input"
              value={amount}
              disabled={busy}
              onChange={(e) => setAmount(e.target.value)}
            />
          </div>
          <div>
            <label className="prop-lbl" htmlFor="inv-due">Due in (days)</label>
            <input
              id="inv-due"
              type="number"
              min="0"
              max="120"
              className="prop-input"
              value={dueInDays}
              disabled={busy}
              onChange={(e) => setDueInDays(e.target.value)}
            />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label className="prop-lbl" htmlFor="inv-ms">Milestone label (optional)</label>
            <input
              id="inv-ms"
              className="prop-input"
              placeholder="e.g. Engagement deposit"
              value={milestone}
              disabled={busy}
              onChange={(e) => setMilestone(e.target.value)}
            />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label className="prop-lbl" htmlFor="inv-notes">Notes (optional)</label>
            <textarea
              id="inv-notes"
              rows={2}
              className="prop-textarea"
              value={notes}
              disabled={busy}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
          {error && (
            <div style={{ gridColumn: '1 / -1', color: 'var(--red)', fontSize: 13 }}>{error}</div>
          )}
          <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 10 }}>
            <button type="button" className="btn" onClick={handleIssue} disabled={busy}>
              {busy ? 'Issuing…' : 'Issue invoice →'}
            </button>
            <button type="button" className="btn ghost" onClick={() => setOpen(false)} disabled={busy}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function DraftEditor({
  form, setForm, feeFloorPence, parentValuePence, busy,
}: {
  form: FormState;
  setForm: (f: FormState | ((prev: FormState) => FormState)) => void;
  feeFloorPence: number | null;
  parentValuePence: number | null;
  busy: boolean;
}) {
  const update = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const updateDeliverable = (i: number, patch: Partial<DeliverableInput>) =>
    setForm((prev) => ({
      ...prev,
      deliverables: prev.deliverables.map((d, idx) => (idx === i ? { ...d, ...patch } : d)),
    }));
  const addDeliverable = () =>
    setForm((prev) => ({ ...prev, deliverables: [...prev.deliverables, { label: '', description: '' }] }));
  const removeDeliverable = (i: number) =>
    setForm((prev) => ({ ...prev, deliverables: prev.deliverables.filter((_, idx) => idx !== i) }));

  const updateMilestone = (i: number, patch: Partial<MilestoneInput>) =>
    setForm((prev) => ({
      ...prev,
      milestones: prev.milestones.map((m, idx) => (idx === i ? { ...m, ...patch } : m)),
    }));
  const addMilestone = () =>
    setForm((prev) => ({ ...prev, milestones: [...prev.milestones, { label: '', amount: '', due_at: '' }] }));
  const removeMilestone = (i: number) =>
    setForm((prev) => ({ ...prev, milestones: prev.milestones.filter((_, idx) => idx !== i) }));

  return (
    <div className="proposal-form">
      <div className="card">
        <h3 className="proposal-section-h">Scope</h3>
        <label className="prop-lbl" htmlFor="scope">Scope (markdown)</label>
        <textarea
          id="scope"
          rows={8}
          className="prop-textarea"
          placeholder="What's in scope. Headings, lists and inline emphasis render in the proposal preview."
          value={form.scope_markdown}
          disabled={busy}
          onChange={(e) => update('scope_markdown', e.target.value)}
        />

        <label className="prop-lbl" style={{ marginTop: 18 }}>Deliverables</label>
        {form.deliverables.map((d, i) => (
          <div key={i} className="prop-row">
            <input
              className="prop-input"
              placeholder="Label (e.g. Capital structure review)"
              value={d.label}
              disabled={busy}
              onChange={(e) => updateDeliverable(i, { label: e.target.value })}
            />
            <input
              className="prop-input"
              placeholder="Description (optional)"
              value={d.description}
              disabled={busy}
              onChange={(e) => updateDeliverable(i, { description: e.target.value })}
            />
            <button
              type="button"
              className="prop-row-x"
              onClick={() => removeDeliverable(i)}
              disabled={busy || form.deliverables.length === 1}
              aria-label="Remove deliverable"
            >
              ×
            </button>
          </div>
        ))}
        <button type="button" className="btn ghost sm" onClick={addDeliverable} disabled={busy}>
          + Add deliverable
        </button>

        <label className="prop-lbl" htmlFor="assumptions" style={{ marginTop: 18 }}>Assumptions (optional)</label>
        <textarea
          id="assumptions"
          rows={3}
          className="prop-textarea"
          placeholder="What we assume to be true, e.g. KYC pack ready by week 2."
          value={form.assumptions}
          disabled={busy}
          onChange={(e) => update('assumptions', e.target.value)}
        />
      </div>

      <div className="card">
        <h3 className="proposal-section-h">Commercials</h3>
        <div className="prop-grid">
          <div>
            <label className="prop-lbl" htmlFor="fee">Fee</label>
            <input
              id="fee"
              type="number"
              inputMode="decimal"
              min="0"
              step="any"
              className="prop-input"
              placeholder="e.g. 50000"
              value={form.fee}
              disabled={busy}
              onChange={(e) => update('fee', e.target.value)}
            />
            <div className="prop-hint">
              Whole units of the selected currency. Stored as pence/cents.
              {feeFloorPence != null && (
                <>
                  {' '}
                  REQ-010 floor: <strong>{formatMoney(feeFloorPence, form.currency)}</strong> (2% of brief value).
                </>
              )}
              {parentValuePence === null && ' Brief has no declared value — fee floor not enforced.'}
            </div>
          </div>
          <div>
            <label className="prop-lbl" htmlFor="currency">Currency</label>
            <select
              id="currency"
              className="prop-input"
              value={form.currency}
              disabled={busy}
              onChange={(e) => update('currency', e.target.value as ProposalCurrency)}
            >
              {ISO_CURRENCY.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="prop-lbl" htmlFor="valid_until">Valid until</label>
            <input
              id="valid_until"
              type="date"
              className="prop-input"
              value={form.valid_until}
              disabled={busy}
              onChange={(e) => update('valid_until', e.target.value)}
            />
            <div className="prop-hint">Auto-expires on this date if the client hasn't responded.</div>
          </div>
        </div>

        <label className="prop-lbl" style={{ marginTop: 18 }}>Payment plan</label>
        <div className="chip-row">
          {PROPOSAL_PAYMENT_PLANS.map((p) => (
            <button
              key={p}
              type="button"
              className={`chip-btn ${form.payment_plan === p ? 'on' : ''}`}
              onClick={() => update('payment_plan', p)}
              disabled={busy}
            >
              {PLAN_LABEL[p]}
            </button>
          ))}
        </div>

        {form.payment_plan === 'milestones' && (
          <div style={{ marginTop: 18 }}>
            <label className="prop-lbl">Milestones</label>
            {form.milestones.map((m, i) => (
              <div key={i} className="prop-row">
                <input
                  className="prop-input"
                  placeholder="Milestone label"
                  value={m.label}
                  disabled={busy}
                  onChange={(e) => updateMilestone(i, { label: e.target.value })}
                />
                <input
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="any"
                  className="prop-input"
                  placeholder="Amount"
                  value={m.amount}
                  disabled={busy}
                  onChange={(e) => updateMilestone(i, { amount: e.target.value })}
                />
                <input
                  type="date"
                  className="prop-input"
                  value={m.due_at}
                  disabled={busy}
                  onChange={(e) => updateMilestone(i, { due_at: e.target.value })}
                />
                <button
                  type="button"
                  className="prop-row-x"
                  onClick={() => removeMilestone(i)}
                  disabled={busy}
                  aria-label="Remove milestone"
                >
                  ×
                </button>
              </div>
            ))}
            <button type="button" className="btn ghost sm" onClick={addMilestone} disabled={busy}>
              + Add milestone
            </button>
            <div className="prop-hint" style={{ marginTop: 6 }}>
              Sum of milestone amounts is not auto-balanced against the headline fee — both ship to the client.
            </div>
          </div>
        )}

        {form.payment_plan === 'success_fee' && (
          <div style={{ marginTop: 18 }}>
            <label className="prop-lbl" htmlFor="sft">Success-fee terms</label>
            <textarea
              id="sft"
              rows={4}
              className="prop-textarea"
              placeholder="Define the outcome that triggers the success fee, the calculation basis and the payable date."
              value={form.success_fee_terms}
              disabled={busy}
              onChange={(e) => update('success_fee_terms', e.target.value)}
            />
            <div className="prop-hint">
              Success-fee structures are FCA-sensitive (REQ-013). UK compliance solicitor must review before send.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ReadOnlyView({ p }: { p: ProposalDto }) {
  return (
    <div className="proposal-form">
      <div className="card">
        <h3 className="proposal-section-h">Scope</h3>
        <p className="proposal-prose">{p.scope_markdown}</p>
        {p.deliverables.length > 0 && (
          <>
            <h4 className="proposal-sub-h">Deliverables</h4>
            <ul className="proposal-list">
              {p.deliverables.map((d, i) => (
                <li key={i}>
                  <strong>{d.label}</strong>
                  {d.description && <span style={{ color: 'var(--fg-soft)' }}> — {d.description}</span>}
                </li>
              ))}
            </ul>
          </>
        )}
        {p.assumptions && (
          <>
            <h4 className="proposal-sub-h">Assumptions</h4>
            <p className="proposal-prose">{p.assumptions}</p>
          </>
        )}
      </div>

      <div className="card">
        <h3 className="proposal-section-h">Commercials</h3>
        <div className="proposal-kv-grid">
          <div className="proposal-kv">
            <span>Fee</span>
            <strong>{formatMoney(p.fee_pence, p.currency)}</strong>
          </div>
          <div className="proposal-kv">
            <span>Payment plan</span>
            <strong>{PLAN_LABEL[p.payment_plan]}</strong>
          </div>
          <div className="proposal-kv">
            <span>Valid until</span>
            <strong>{p.valid_until.slice(0, 10)}</strong>
          </div>
          {p.sent_at && (
            <div className="proposal-kv">
              <span>Sent</span>
              <strong>{p.sent_at.slice(0, 10)}</strong>
            </div>
          )}
          {p.accepted_at && (
            <div className="proposal-kv">
              <span>Accepted</span>
              <strong>{p.accepted_at.slice(0, 10)}</strong>
            </div>
          )}
          {p.declined_at && (
            <div className="proposal-kv">
              <span>Declined</span>
              <strong>{p.declined_at.slice(0, 10)}</strong>
            </div>
          )}
        </div>

        {p.payment_plan === 'milestones' && p.milestones.length > 0 && (
          <>
            <h4 className="proposal-sub-h">Milestones</h4>
            <ul className="proposal-list">
              {p.milestones.map((m, i) => (
                <li key={i}>
                  <strong>{m.label}</strong>
                  <span style={{ color: 'var(--fg-soft)' }}> — {formatMoney(m.amount_pence, p.currency)}</span>
                  {m.due_at && <span style={{ color: 'var(--fg-mute)' }}> · due {m.due_at.slice(0, 10)}</span>}
                </li>
              ))}
            </ul>
          </>
        )}
        {p.payment_plan === 'success_fee' && p.success_fee_terms && (
          <>
            <h4 className="proposal-sub-h">Success-fee terms</h4>
            <p className="proposal-prose">{p.success_fee_terms}</p>
          </>
        )}

        {p.decline_reason && (
          <>
            <h4 className="proposal-sub-h">Decline reason</h4>
            <p className="proposal-prose" style={{ color: 'var(--fg-soft)' }}>{p.decline_reason}</p>
          </>
        )}
      </div>
    </div>
  );
}
